import { ArrowLeft, BarChart3, Building2, FileText, FolderTree, Server, Sparkles, Users } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Organization, OrganizationUpdate } from '../../types';
import organizationService from '../../services/organizationService';
import configurationService from '../../services/configurationService';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { Button } from '@/components/Common/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/Common/Card';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import { PageLayout } from '@/components/Layout/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Common/tabs';
import { OrganizationAnalysisLimitsCard } from '@/components/AnalysisLimits/OrganizationAnalysisLimitsCard';
import { useTranslation } from 'react-i18next';
import {
  OrganizationAI,
  OrganizationForge,
  OrganizationMembers,
  OrganizationOverview,
  OrganizationStructureTree,
  OrganizationTemplates,
} from '@/components/Organizations';

const OrganizationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSectorChangeConfirm, setShowSectorChangeConfirm] = useState(false);
  const [tab, setTab] = useState('overview');

  // Super admins can always edit
  const canEdit = true;

  // Form state
  const [formData, setFormData] = useState<OrganizationUpdate>({
    name: '',
    description: '',
    domain: '',
    sector_template_id: '',
    is_active: true
  });

  useEffect(() => {
    if (id) {
      loadOrganization();
    }
  }, [id]);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        domain: organization.domain || '',
        sector_template_id: organization.sector_template_id || '',
        is_active: organization.is_active
      });
    }
  }, [organization]);

  const loadOrganization = async () => {
    if (!id) return;
    try {
      setError(null);
      // Fetch single org by id (backend returns it regardless of is_active for super_admin)
      const org = await organizationService.getOrganizationById(id);
      setOrganization(org);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(t('organizationsPage.notFound'));
      } else {
        setError(t('organizationsPage.loadError'));
      }
      console.error('Error loading organization:', err);
    }
  };

  const saveOrganization = async (sectorChanged: boolean) => {
    if (!organization) return;

    try {
      setSaving(true);
      setError(null);
      const updated = await organizationService.updateOrganization(organization.id, formData);
      setOrganization(updated);
      if (sectorChanged && formData.sector_template_id) {
        try {
          await configurationService.loadTemplate(organization.id, formData.sector_template_id);
        } catch (templateError) {
          setError(
            t(
              'organizations.page.sectorChangeTemplateLoadError',
              "Le secteur a bien été modifié, mais la réinitialisation automatique de la configuration a échoué."
            )
          );
          console.error('Error reloading sector template after organization update:', templateError);
        }
      }
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error updating organization:', err);
      setError(err.response?.data?.detail || t('organizationsPage.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;

    const sectorChanged = (formData.sector_template_id || '') !== (organization.sector_template_id || '');
    if (sectorChanged) {
      setShowSectorChangeConfirm(true);
      return;
    }

    await saveOrganization(false);
  };

  const handleConfirmSectorChange = async () => {
    setShowSectorChangeConfirm(false);
    await saveOrganization(true);
  };

  const handleCancel = () => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        domain: organization.domain || '',
        sector_template_id: organization.sector_template_id || '',
        is_active: organization.is_active
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const backBtn = (
    <Button variant="outline" size="sm" onClick={() => navigate('/organizations')}>
      <ArrowLeft className="h-4 w-4 mr-2" />
      {t('global.back', 'Back')}
    </Button>
  );

  if (error && !organization) {
    return (
      <PageLayout
        icon={<Building2 className="h-6 w-6" />}
        title={t('sidebar.organizations.root')}
        subtitle=""
        headerRight={backBtn}
      >
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive text-base">{t('global.error')}</CardTitle>
            <CardDescription className="text-destructive/90">{error}</CardDescription>
          </CardHeader>
        </Card>
      </PageLayout>
    );
  }

  if (!organization) {
    return (
      <PageLayout
        icon={<Building2 className="h-6 w-6" />}
        title={t('sidebar.organizations.root')}
        subtitle=""
        headerRight={backBtn}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      icon={<Building2 className="h-6 w-6" />}
      title={organization.name}
      subtitle={organization.description || ''}
      headerRight={backBtn}
    >
      {error && (
        <Card className="border-destructive/40 mb-4">
          <CardHeader>
            <CardTitle className="text-destructive text-base">{t('global.error')}</CardTitle>
            <CardDescription className="text-destructive/90">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <Tabs defaultValue="overview" value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex w-full space-x-2 rounded-xl bg-muted p-1 h-auto mb-4">
          <TabsTrigger
            value="overview"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            {t('organizationDetails.tabs.overview', 'Aperçu')}
          </TabsTrigger>
          <TabsTrigger
            value="structure"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <FolderTree className="h-4 w-4" />
            {t('organizationDetails.tabs.structure', 'Structure')}
          </TabsTrigger>
          {forgeApiEnabled && (
            <TabsTrigger
              value="forge"
              className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
            >
              <Server className="h-4 w-4" />
              {t('organizationDetails.tabs.forge', 'Forge')}
            </TabsTrigger>
          )}
          <TabsTrigger
            value="ai"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {t('organizationDetails.tabs.ai', 'IA')}
          </TabsTrigger>
          <TabsTrigger
            value="members"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <Users className="h-4 w-4" />
            {t('organizationDetails.tabs.members', 'Membres')}
          </TabsTrigger>
          <TabsTrigger
            value="templates"
            className="w-full rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2"
          >
            <FileText className="h-4 w-4" />
            {t('organizationDetails.tabs.templates', 'Templates')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OrganizationOverview
            organization={organization}
            canEdit={canEdit}
            isEditing={isEditing}
            formData={formData}
            saving={saving}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
            onFormChange={setFormData}
            onOpenMembers={() => setTab('members')}
          />
        </TabsContent>

        <TabsContent value="structure">
          <OrganizationStructureTree organization={organization} />
        </TabsContent>

        {forgeApiEnabled && (
          <TabsContent value="forge">
            <OrganizationForge organization={organization} />
          </TabsContent>
        )}

        <TabsContent value="ai">
          <div className="space-y-4">
            <OrganizationAI organization={organization} />
            {/* Per-organization override of the project analysis limits (super admin page). */}
            <OrganizationAnalysisLimitsCard organizationId={organization.id} />
          </div>
        </TabsContent>

        <TabsContent value="members">
          <OrganizationMembers organization={organization} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="templates">
          <OrganizationTemplates />
        </TabsContent>
      </Tabs>

      <ConfirmationModal
        open={showSectorChangeConfirm}
        title={t('organizations.modals.edit.alerts.sectorChangeTitle', 'Confirmer le changement de secteur')}
        message={t(
          'organizations.modals.edit.alerts.sectorChangeResetsKnowledge',
          "Changer le secteur va écraser la configuration de connaissance existante avec les fichiers par défaut de ce secteur. Continuer ?"
        )}
        confirmText={t('global.confirm', 'Confirmer')}
        cancelText={t('global.cancel')}
        onConfirm={handleConfirmSectorChange}
        onCancel={() => setShowSectorChangeConfirm(false)}
        loading={saving}
      />
    </PageLayout>
  );
};

export default OrganizationDetailsPage;
